<template>
  <li :class="[{ selected: selectedEnhancementData }]">
    <div
      data-component="enhancements-card"
      class="enhancements-card"
      :class="{ selected: selectedEnhancementData, 'have-error': haveError, 'disabled': isDisabled }"
    >
      <div class="enhancements-card-container selected">
        <div
          v-if="!haveIcon"
          class="enhancements-card-image"
        >
          <picture
            v-lazyload="{
              src: enhancement.icon,
              set: [
                { width: '160', height: '160', breakpoint: 'xs' },
                { width: '800', height: '360', breakpoint: 'lg' },
              ],
              fit: 'crop',
              align: 'center',
            }"
            class="image-wrapper"
          >
            <img
              class="image"
              src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
              :alt="enhancement.name"
            >
          </picture>
        </div>
        <div v-else>
          <img
            :src="enhancement.icon"
            :alt="enhancement.name"
          >
        </div>
        <div class="enhancements-card-wrapper">
          <div class="enhancements-card-body">
            <div class="enhancements-card-title">
              {{ enhancement.name }}
            </div>
            <div v-if="enhancementShortDescription && !enhancementSummaryShort" class="enhancements-card-short-description">{{ enhancementShortDescription }}</div>
            <div v-if="enhancementSummaryShort && enhancement.userInput !== 'No input'" v-html="enhancementSummaryShort" class="enhancements-card-short-summary" />
            <div
              class="enhancements-card-details"
              v-if="enhancement.userInput !== 'No input' && selectedEnhancementData"
            >
              <div class="enhancements-card-ctas">
                <button
                  class="edit"
                  :disabled="cartPending"
                  @click="$emit('edit-enhancement', { ...enhancement, ...selectedEnhancementData })">
                  {{ haveError ? tc('enhancements.actionRequired') :tc('booking.editDetails') }}
                </button>
              </div>
            </div>
          </div>
          <button
            :id="`enhancement-${enhancement.sitecoreId}`"
            class="enhancements-card-button"
            :class="{ selected: selectedEnhancementData }"
            :disabled="isDisabled"
            :aria-disabled="isDisabled"
            :aria-pressed="selectedEnhancementData ? 'true' : 'false'"
            :aria-label="selectedEnhancementData
              ? `${tc('booking.remove')} ${enhancement.name}`
              : `${tc('booking.add')} ${enhancement.name}`"
            @click.prevent="handleClick"
            @keypress.enter.prevent="handleClick"
            @keypress.space.prevent="handleClick"
          >
            <div v-if="haveError">
              <svg width="26px" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.2088 4.19121L2.33547 19.0045C2.15252 19.3214 2.05572 19.6806 2.0547 20.0464C2.05367 20.4123 2.14846 20.772 2.32963 21.0899C2.5108 21.4077 2.77204 21.6726 3.08735 21.8581C3.40267 22.0436 3.76107 22.1434 4.1269 22.1474H21.8736C22.2394 22.1434 22.5978 22.0436 22.9131 21.8581C23.2284 21.6726 23.4897 21.4077 23.6708 21.0899C23.852 20.772 23.9468 20.4123 23.9458 20.0464C23.9447 19.6806 23.8479 19.3214 23.665 19.0045L14.7917 4.19121C14.6049 3.88332 14.3419 3.62876 14.0281 3.45209C13.7144 3.27543 13.3603 3.18262 13.0002 3.18262C12.6401 3.18262 12.2861 3.27543 11.9723 3.45209C11.6585 3.62876 11.3956 3.88332 11.2088 4.19121V4.19121Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13 9.57617V13.7666" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13 17.957H13.0105" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div
              v-else-if="!selectedEnhancementData"
              :class="`icon-${isLoading && cartPending ? 'loading-black' : 'add'}`"
            />
            <div
              v-else
              :class="`icon-${isLoading && cartPending ? 'loading' : 'checkmark'}`"
            />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="haveError"
      class="enhancements-card-error"
    >
      {{ compPrivilegeErrorMessage }}
    </div>
  </li>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'

  const NO_INPUT_ENHANCEMENT = 'No input'

  export default {
    name: 'EnhancementCard',
    props: {
      enhancement: {
        type: Object,
        required: true,
      },
      numberOfSelectedEnhancements: {
        type: Number,
        required: true
      },
      ignoreMaxNumber: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      isLoading: false,
    }),
    computed: {
      ...mapGetters("i18n", ["tc"]),
      ...mapGetters('cart', ['enhancementsInRoom']),
      ...mapGetters("auth", ["enhancementsAllowed"]),
      ...mapGetters('modifyReservation', ['storedModifyBookingPropData']),
      ...mapState('cart', ['cartPending']),
      maxEnhancementsSelected() {
        return  !this.ignoreMaxNumber && (this.numberOfSelectedEnhancements >= this.enhancementsAllowed);
      },
      selectedEnhancementData() {
        return this.enhancementsInRoom.find(
          (e) => e.sitecoreId === this.enhancement.sitecoreId
        )
      },
      haveIcon() {
        return !!this.enhancement.icon.split('is/content')[1]
      },
      isDisabled() {
        return (this.maxEnhancementsSelected && !this.selectedEnhancementData ) || this.cartPending
      },
      compPrivilegeErrorMessage() {
        return (
          this.storedModifyBookingPropData
            ?.ModifyReservationCompPrivilegeErrorMessage ?? ''
        )
      },
      enhancementShortDescription() {
        return this.enhancement?.shortDescription ?? ''
      },
      enhancementSummary() {
        return this.selectedEnhancementData?.summary ?? ''
      },
      enhancementSummaryShort() {
        return this.selectedEnhancementData?.summaryShort ?? ''
      },
      haveError() {
        return this.enhancement.userInput !== NO_INPUT_ENHANCEMENT && this.selectedEnhancementData && this.enhancementSummary === ''
      }
    },
    watch: {
      cartPending(val) {
        if (!val) {
          this.isLoading = val
        }
      },
      selectedEnhancementData(newValue, oldValue) {
        if (!newValue || !oldValue) {
          this.$nextTick(() => {
            const button = document.getElementById(`enhancement-${this.enhancement.sitecoreId}`)
            if (button) {
              button.focus()
            }
          })
        }
      }
    },
    methods: {
      handleClick() {
        if (this.selectedEnhancementData) {
          this.removeEnhancement()
        } else {
          this.selectEnhancement()
        }
      },
      selectEnhancement() {
        this.isLoading = true
        this.$emit('select-enhancement', this.enhancement)
      },
      removeEnhancement() {
        this.isLoading = true
        this.$emit('remove-enhancement', this.selectedEnhancementData?.sitecoreId)
      },
    },
  }
</script>
